import React, { useEffect, useState } from 'react';
import styles from '../styles/Loader.module.css';

const TICK_MS = 200;
const PARTICLE_COUNT = 6;

/**
 * Time-based progress for work whose real progress we can't observe (a single
 * LLM call that either hasn't returned or has). Runs 0→90% linearly across the
 * estimate, then creeps 90→99% asymptotically so an overrun still feels alive
 * and never claims to be finished.
 */
function estimatedProgress(elapsedMs, estimatedMs) {
  const ratio = elapsedMs / estimatedMs;
  if (ratio < 1) return ratio * 90;
  return 90 + (1 - Math.exp(-(ratio - 1) * 1.2)) * 9;
}

/**
 * Loader — the app's single loading primitive.
 *
 * Consolidates what used to be four separate, visually inconsistent loaders
 * (LoadingScreen, FullScreenLoader, the onboarding progress bar and
 * ThinkingIndicator's dots). Following the repo's Unified Component Pattern,
 * one file carries every presentation via props.
 *
 * Two independent axes:
 *
 *   variant   where it sits
 *     'inline'     (default) compact block inside a card, list or detail view
 *     'fullscreen' fills the viewport but stays in normal document flow
 *     'overlay'    fixed over everything, on the deep brand teal
 *
 *   indicator what animates
 *     'spinner' (default) | 'dots' | 'progress'
 *
 *   <Loader text="Loading article…" indicator="dots" />
 *   <Loader variant="fullscreen" indicator="dots" text={message} />
 *   <Loader variant="overlay" text="Building your plan…" estimatedMs={45000} />
 *
 * Props:
 *  - text: the headline status line
 *  - subtext: optional smaller reassurance line underneath
 *  - variant / indicator: see above
 *  - progress: 0-100, for indicator="progress"
 *  - showProgressText: show the numeric "42%" under the bar (default true;
 *    suppressed automatically when estimatedMs drives its own countdown)
 *  - estimatedMs: how long this usually takes. When set, the loader switches
 *    to a self-driven progress bar and counts the estimate down, instead of
 *    showing an indicator against an unknown wait.
 *  - onDark: invert text/rail colours for a brand-filled surface. Defaults to
 *    true for variant="overlay", which is always dark.
 *  - className: extra class on the root, for one-off layout nudges.
 */
function Loader({
  text,
  subtext,
  variant = 'inline',
  indicator = 'spinner',
  progress = 0,
  showProgressText = true,
  estimatedMs = 0,
  onDark,
  className = ''
}) {
  const [elapsedMs, setElapsedMs] = useState(0);
  const timed = estimatedMs > 0;

  useEffect(() => {
    if (!timed) return undefined;

    const startedAt = Date.now();
    const interval = setInterval(() => setElapsedMs(Date.now() - startedAt), TICK_MS);
    return () => clearInterval(interval);
  }, [timed]);

  const isDark = onDark === undefined ? variant === 'overlay' : onDark;
  const shownIndicator = timed ? 'progress' : indicator;
  const shownProgress = timed ? estimatedProgress(elapsedMs, estimatedMs) : progress;
  const clamped = Math.min(100, Math.max(0, shownProgress));

  // Ambient particles are only worth the paint on the two large surfaces.
  const showParticles = variant === 'fullscreen' || variant === 'overlay';

  const remainingSec = timed ? Math.ceil((estimatedMs - elapsedMs) / 1000) : 0;
  const timeLine = remainingSec > 0
    ? `About ${remainingSec}s remaining`
    : 'Almost there — thanks for hanging on';

  const rootClass = [
    styles.loader,
    styles[variant] || '',
    isDark ? styles.onDark : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClass} role="status" aria-live="polite">
      {showParticles && (
        <div className={styles.particles} aria-hidden="true">
          {Array.from({ length: PARTICLE_COUNT }).map((_, i) => (
            <span key={i} className={styles.particle} />
          ))}
        </div>
      )}

      <div className={styles.content}>
        {text && <div className={styles.text}>{text}</div>}

        {shownIndicator === 'dots' && (
          <div className={styles.dots} aria-hidden="true">
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
          </div>
        )}

        {shownIndicator === 'progress' && (
          <div className={styles.progressWrap}>
            <div
              className={styles.progressTrack}
              role="progressbar"
              aria-valuenow={Math.round(clamped)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className={styles.progressFill} style={{ width: `${clamped}%` }} />
            </div>
            {showProgressText && !timed && (
              <div className={styles.progressText}>{Math.round(clamped)}%</div>
            )}
          </div>
        )}

        {shownIndicator === 'spinner' && <div className={styles.spinner} aria-hidden="true" />}

        {/* aria-hidden: the countdown reruns every tick and would spam the
            aria-live region; the headline already carries the status. */}
        {timed && <p className={styles.timeLine} aria-hidden="true">{timeLine}</p>}

        {subtext && <p className={styles.subtext}>{subtext}</p>}
      </div>
    </div>
  );
}

export default Loader;
